<template>
  <financa-table-viewport :is-reloading="loading && rows.length">
      <table class="fin-table" :class="{ 'is-grouped': isGrouped }">
        <thead>
          <tr>
            <th class="fin-col-product" :rowspan="headerRows">
              <button type="button" class="fin-link-btn" @click="$emit('sort', 'name')">
                Produkti
                <i class="fa" :class="sortIcon('name')"></i>
              </button>
            </th>
            <th
              v-for="group in displayGroups"
              :key="group.id"
              class="fin-col-group"
              :class="{
                'fin-col-highlighted': isLeafHighlighted(group),
                'fin-col-clickable': isMagazineColumn(group),
              }"
              :colspan="groupColspan(group)"
              :title="columnHeaderTitle(group)"
              @click="toggleColumnHighlight(group)"
            >
              {{ group.name }}
            </th>
            <th class="fin-col-group fin-total-cell" :colspan="subColumnCount">
              <button type="button" class="fin-link-btn" @click="$emit('sort', metric === 'value' ? 'value' : 'qty')">
                Totali
                <i class="fa" :class="sortIcon(metric === 'value' ? 'value' : 'qty')"></i>
              </button>
            </th>
          </tr>
          <tr v-if="isGrouped">
            <template v-for="group in displayGroups" :key="group.id + '-wh'">
              <th
                v-for="leaf in group.children"
                :key="leaf.id"
                class="fin-col-group"
                :class="{
                  'fin-col-highlighted': isLeafHighlighted(leaf),
                  'fin-col-clickable': isMagazineColumn(leaf),
                  'fin-total-cell': leaf.is_group_total,
                }"
                :colspan="subColumnCount"
                :title="columnHeaderTitle(leaf)"
                @click="toggleColumnHighlight(leaf)"
              >
                {{ leaf.name }}
              </th>
            </template>
            <th
              v-for="part in metricParts"
              :key="'total-group-' + part"
              class="fin-total-cell"
              :class="{ 'fin-col-group': isFirstPart(part) }"
            >
              {{ partLabel(part) }}
            </th>
          </tr>
          <tr v-if="needsMetricRow">
            <template v-for="leaf in leafColumns" :key="leaf.id + '-metric'">
              <th
                v-for="part in metricParts"
                :key="leaf.id + '-' + part"
                :class="{
                  'fin-col-group': isFirstPart(part),
                  'fin-col-highlighted': isLeafHighlighted(leaf),
                  'fin-col-clickable': isMagazineColumn(leaf),
                  'fin-total-cell': leaf.is_group_total,
                }"
                :title="columnHeaderTitle(leaf)"
                @click="toggleColumnHighlight(leaf)"
              >
                {{ partLabel(part) }}
              </th>
            </template>
            <th
              v-for="part in metricParts"
              :key="'total-metric-' + part"
              class="fin-total-cell"
              :class="{ 'fin-col-group': isFirstPart(part) }"
            >
              {{ partLabel(part) }}
            </th>
          </tr>
        </thead>

        <tbody v-if="loading && !rows.length">
          <tr v-for="n in 8" :key="'sk-' + n" class="fin-skeleton-row">
            <td class="fin-col-product">
              <div class="fin-skeleton-line fin-skeleton-line--lg"></div>
              <div class="fin-skeleton-line fin-skeleton-line--sm" style="margin-top: 6px"></div>
            </td>
            <td v-for="i in skeletonCellCount" :key="'skc-' + n + '-' + i" style="text-align: center">
              <span class="fin-skeleton-line fin-skeleton-line--xs"></span>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr v-for="row in rows" :key="row.code" @click="$emit('open-history', row)" style="cursor: pointer" title="Kliko për historikun e produktit">
            <td class="fin-col-product">
              <div class="fin-product-name-row">
                <div class="fin-product-name">{{ row.name }}</div>
                <button
                  v-if="canOpenProduct && row.guid"
                  type="button"
                  class="fin-product-kartela-btn"
                  title="Hap kartelën e produktit"
                  @click.stop="$emit('open-product', row)"
                >
                  <i class="fa fa-pencil"></i>
                </button>
              </div>
              <div class="fin-product-meta">{{ row.code }}<span v-if="row.barcode"> · {{ row.barcode }}</span></div>
            </td>
            <template v-for="leaf in leafColumns" :key="row.code + leaf.id">
              <td
                v-for="part in metricParts"
                :key="row.code + leaf.id + part"
                :style="{ textAlign: part === 'qty' ? 'center' : 'right' }"
                :class="{ 'fin-col-group': isFirstPart(part), 'fin-col-highlighted': isLeafHighlighted(leaf), 'fin-total-cell': leaf.is_group_total }"
              >
                <span :class="partClass(part, cellPart(row, leaf, part))">{{ formatPart(part, cellPart(row, leaf, part)) }}</span>
              </td>
            </template>
            <td
              v-for="part in metricParts"
              :key="row.code + '-total-' + part"
              class="fin-total-cell"
              :class="{ 'fin-col-group': isFirstPart(part) }"
              :style="{ textAlign: part === 'qty' ? 'center' : 'right' }"
            >
              {{ formatPart(part, totalPart(row, part)) }}
            </td>
          </tr>
        </tbody>

        <tfoot v-if="rows.length">
          <tr>
            <td class="fin-col-product">Totali i filtruar ({{ totals.products || 0 }} produkte)</td>
            <td :colspan="leafColumns.length * subColumnCount"></td>
            <td
              v-for="part in metricParts"
              :key="'footer-' + part"
              :style="{ textAlign: part === 'qty' ? 'center' : 'right' }"
            >
              {{ part === 'cost' ? '—' : formatPart(part, footerPart(part)) }}
            </td>
          </tr>
        </tfoot>
      </table>

      <div v-if="loading && rows.length" class="fin-table-overlay" aria-hidden="true"></div>
      <div v-else-if="!loading && !rows.length" class="fin-empty">{{ emptyHint }}</div>

    <template #footer>
      <pagination-bar :pagination="pagination" @page="$emit('page', $event)" @per-page="$emit('per-page', $event)" />
    </template>
  </financa-table-viewport>
</template>

<script>
import PaginationBar from "./PaginationBar.vue";
import FinancaTableViewport from "./FinancaTableViewport.vue";
import formatMixin from "../shared/formatMixin.js";
import columnHighlightMixin from "../shared/columnHighlightMixin.js";

export default {
  name: "FinancaStockTable",
  components: { PaginationBar, FinancaTableViewport },
  mixins: [formatMixin, columnHighlightMixin],
  props: {
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    totals: { type: Object, default: () => ({}) },
    pagination: { type: Object, default: () => ({}) },
    metric: { type: String, default: "qty" },
    loading: { type: Boolean, default: false },
    sort: { type: String, default: "name" },
    sortDir: { type: String, default: "asc" },
    emptyHint: { type: String, default: "Nuk u gjet asnjë produkt me këto filtra." },
    canOpenProduct: { type: Boolean, default: false },
  },
  emits: ["page", "per-page", "sort", "open-history", "open-product"],
  computed: {
    showQty() {
      return true;
    },
    showCost() {
      return this.metric === "both";
    },
    showValue() {
      return this.metric === "both";
    },
    metricParts() {
      const parts = [];
      if (this.showQty) parts.push("qty");
      if (this.showCost) parts.push("cost");
      if (this.showValue) parts.push("value");
      return parts.length ? parts : ["qty"];
    },
    subColumnCount() {
      return this.metricParts.length;
    },
    isGrouped() {
      return (this.columns || []).some((column) => Array.isArray(column.children) && column.children.length);
    },
    displayGroups() {
      if (this.isGrouped) return this.columns;
      return (this.columns || []).map((column) => ({ ...column, children: [column] }));
    },
    leafColumns() {
      return this.displayGroups.flatMap((group) => group.children || [group]);
    },
    needsMetricRow() {
      if (this.showValue) return true;
      if (this.isGrouped) return this.subColumnCount > 1;
      return true;
    },
    headerRows() {
      return 1 + (this.isGrouped ? 1 : 0) + (this.needsMetricRow ? 1 : 0);
    },
    skeletonCellCount() {
      return Math.max(2, this.leafColumns.length * this.subColumnCount + this.subColumnCount);
    },
  },
  methods: {
    sortIcon(field) {
      if (this.sort !== field) return "fa-sort";
      return this.sortDir === "asc" ? "fa-sort-up" : "fa-sort-down";
    },
    groupColspan(group) {
      return (group.children || [group]).length * this.subColumnCount;
    },
    isFirstPart(part) {
      return part === this.metricParts[0];
    },
    partLabel(part) {
      if (part === "qty") return "Sasia";
      if (part === "cost") return "Kosto";
      return "Vlera";
    },
    cellQty(row, leaf) {
      return (row.cells && row.cells[leaf.id] && row.cells[leaf.id].qty) || 0;
    },
    cellValue(row, leaf) {
      return (row.cells && row.cells[leaf.id] && row.cells[leaf.id].value) || 0;
    },
    cellCost(row, leaf) {
      const cell = row.cells && row.cells[leaf.id];
      if (!cell) return 0;
      if (cell.unit_cost != null && cell.unit_cost !== "") return Number(cell.unit_cost) || 0;
      if (Number(cell.qty)) return Number(cell.value || 0) / Number(cell.qty);
      return 0;
    },
    cellPart(row, leaf, part) {
      if (part === "qty") return this.cellQty(row, leaf);
      if (part === "cost") return this.cellCost(row, leaf);
      return this.cellValue(row, leaf);
    },
    totalPart(row, part) {
      if (part === "qty") return (row.total && row.total.qty) || 0;
      if (part === "cost") return Number(row.unit_cost || 0);
      return (row.total && row.total.value) || 0;
    },
    footerPart(part) {
      if (part === "qty") return this.totals.qty || 0;
      return this.totals.value || 0;
    },
    formatPart(part, value) {
      return part === "qty" ? this.formatQty(value) : this.formatValue(value);
    },
    partClass(part, value) {
      if (!value) return "fin-cell-zero";
      return part === "qty" ? "fin-cell-qty" : "fin-cell-value";
    },
  },
};
</script>
